import { Check, Minus, MessageCircle, ShieldCheck } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { waLink } from "@/data/site";
import { useLang } from "@/i18n";

const PLANS_META = [
  { name: "Launch", price: "₹4,999", featured: false },
  { name: "Business", price: "₹6,999", featured: true },
  { name: "Business Pro", price: "₹9,999", featured: false },
];

const Cell = ({ v }) =>
  v === true ? (
    <Check className="mx-auto h-5 w-5 text-[#4A362E]" />
  ) : v === false ? (
    <Minus className="mx-auto h-4 w-4 text-[#D7B594]" />
  ) : (
    <span className="text-sm font-medium text-[#3E2723]">{v}</span>
  );

const Pricing = () => {
  const t = useLang().t;
  const plans = PLANS_META.map((m, i) => ({ ...m, ...t.pricing.plans[i] }));
  return (
  <section id="pricing" data-testid="pricing" className="bg-[#FDFBF7] py-16 sm:py-24">
    <div className="container-site">
      <SectionHead
        testId="pricing-head"
        eyebrow={t.pricing.eyebrow}
        title={t.pricing.title}
        sub={t.pricing.sub}
      />

      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        {plans.map((p) => (
          <div
            key={p.name}
            data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative flex flex-col rounded-2xl border bg-white p-6 sm:p-8 transition-shadow duration-200 hover:shadow-md ${
              p.featured ? "border-[#A88667] shadow-md ring-1 ring-[#A88667]" : "border-[#EFEBE4] shadow-sm"
            }`}
          >
            {p.featured && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#4A362E] px-4 py-1 text-xs font-bold uppercase tracking-wider text-white">
                {t.pricing.recommended}
              </span>
            )}
            <p className="text-sm font-semibold uppercase tracking-wider text-[#A88667]">{p.name}</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-4xl font-bold text-[#3E2723]">{p.price}</span>
              <span className="text-sm text-[#8D6E63]">{t.pricing.oneTime}</span>
            </div>
            <p className="mt-1 font-heading text-sm font-semibold text-[#5D4037]">{p.positioning}</p>
            <p className="mt-1 text-sm text-[#8D6E63]">{p.note}</p>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-[#8D6E63]">{p.tag}</p>
            <ul className="mt-3 flex-1 space-y-2.5">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-[#3E2723]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#A88667]" /> {f}
                </li>
              ))}
            </ul>
            <a
              href={waLink(`Hi Anjum, I am interested in the ${p.name} package (${p.price}) for my business website.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`${p.featured ? "btn-primary" : "btn-outline"} mt-8 w-full`}
            >
              {p.cta}
            </a>
          </div>
        ))}
      </div>
      <p className="mx-auto mt-6 max-w-3xl text-center text-sm text-[#8D6E63]">
        {t.pricing.domainNote}
      </p>

      {/* Comparison table */}
      <div className="mt-20" data-testid="pricing-comparison">
        <h3 className="text-center font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]">{t.pricing.compareTitle}</h3>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-[#EFEBE4] bg-white shadow-sm">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-[#EFEBE4] bg-[#F5F2EB]">
                <th className="px-5 py-4 font-heading text-sm font-bold text-[#3E2723]">{t.pricing.featureCol}</th>
                <th className="px-5 py-4 text-center font-heading text-sm font-bold text-[#3E2723]">Launch</th>
                <th className="px-5 py-4 text-center font-heading text-sm font-bold text-[#A88667]">Business</th>
                <th className="px-5 py-4 text-center font-heading text-sm font-bold text-[#3E2723]">Business Pro</th>
              </tr>
            </thead>
            <tbody>
              {t.pricing.comparison.map(([feature, a, b, c], i) => (
                <tr key={feature} className={i % 2 ? "bg-[#FDFBF7]" : "bg-white"}>
                  <td className="px-5 py-3 text-sm text-[#3E2723]">{feature}</td>
                  <td className="px-5 py-3 text-center"><Cell v={a} /></td>
                  <td className="px-5 py-3 text-center bg-[#F5F2EB]/60"><Cell v={b} /></td>
                  <td className="px-5 py-3 text-center"><Cell v={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Annual maintenance */}
      <div className="mt-20 grid gap-6 lg:grid-cols-2">
        <div data-testid="maintenance-card" className="rounded-2xl border border-[#EFEBE4] bg-[#F5F2EB] p-6 sm:p-8">
          <p className="eyebrow">{t.pricing.maintenance.eyebrow}</p>
          <h3 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]">{t.pricing.maintenance.title}</h3>
          <p className="mt-3 font-heading text-3xl font-bold text-[#A88667]">₹1,999 <span className="text-base font-medium text-[#8D6E63]">{t.pricing.maintenance.perYear}</span></p>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            {t.pricing.maintenance.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-[#3E2723]">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#A88667]" /> {f}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-xs leading-relaxed text-[#8D6E63]">
            {t.pricing.maintenance.note}
          </p>
        </div>

        <div data-testid="optional-services" className="rounded-2xl border border-[#EFEBE4] bg-white p-6 sm:p-8 shadow-sm">
          <p className="eyebrow">{t.pricing.optional.eyebrow}</p>
          <h3 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]">{t.pricing.optional.title}</h3>
          <ul className="mt-5 space-y-4">
            {t.pricing.optional.items.map((o) => (
              <li key={o.name} className="flex items-baseline justify-between gap-4 border-b border-[#EFEBE4] pb-3 last:border-0 last:pb-0">
                <span className="text-sm font-medium text-[#3E2723]">{o.name}</span>
                <span className="text-right text-sm font-semibold text-[#A88667]">
                  {o.price}
                  {o.note && <span className="block text-xs font-normal text-[#8D6E63]">{o.note}</span>}
                </span>
              </li>
            ))}
          </ul>
          <a
            href={waLink("Hi Anjum, I would like to know more about your optional services (email, extra pages, content writing, logo design).")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="optional-services-whatsapp"
            className="btn-whatsapp mt-6 w-full"
          >
            <MessageCircle className="h-4 w-4" /> {t.pricing.optional.cta}
          </a>
        </div>
      </div>

      {/* Trust section */}
      <div data-testid="pricing-trust" className="mt-20 rounded-3xl bg-[#3E2723] px-6 py-14 text-center sm:px-12">
        <ShieldCheck className="mx-auto h-10 w-10 text-[#D7B594]" />
        <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-bold text-[#F5F2EB]">{t.pricing.trust.title}</h3>
        <p className="mt-4 font-heading text-xl sm:text-2xl font-semibold text-[#D7B594]">{t.pricing.trust.big}</p>
        <p className="mx-auto mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-[#E7DED2]/80">
          {t.pricing.trust.body}
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-xs text-[#E7DED2]/60">
          {t.pricing.trust.clarification}
        </p>
        <a
          href={waLink("Hi Anjum, I am interested in your website packages and would like to discuss which one is right for my business.")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pricing-trust-whatsapp"
          className="btn-whatsapp mt-8"
        >
          <MessageCircle className="h-4 w-4" /> {t.pricing.trust.cta}
        </a>
      </div>
    </div>
  </section>
  );
};

export default Pricing;
